/**
 * Shared password validation logic used across register, profile, reset/update password, and ChangePasswordDialog.
 * Rules: min 8 characters, at least 1 lowercase, 1 uppercase, 1 number, 1 special character (excluding &).
 */
import * as Yup from 'yup';

export const DEFAULT_MIN_LENGTH = 8;

/**
 * Returns requirement flags for a given password (for UI checkmarks).
 * @param {string} password
 * @param {number} [minLength=8]
 * @returns {{ length: boolean, number: boolean, uppercase: boolean, special: boolean, lowercase: boolean }}
 */
export function checkPasswordRequirements(password, minLength = DEFAULT_MIN_LENGTH) {
  if (!password)
    return { length: false, number: false, uppercase: false, special: false, lowercase: false };
  return {
    length: password.length >= minLength,
    number: /[0-9]/.test(password),
    uppercase: /[A-Z]/.test(password),
    special: /[^a-zA-Z0-9&]/.test(password),
    lowercase: /[a-z]/.test(password),
  };
}

/**
 * Yup validation for a new password field (register, activate, reset, update, profile new password).
 * @param {function} t - i18n t
 * @param {{ minLength?: number, requiredLabel?: string }} [opts] - optional: minLength (default 8), requiredLabel (e.g. nouns.new_password)
 */
export function createNewPasswordValidation(t, opts = {}) {
  const minLength = opts.minLength ?? DEFAULT_MIN_LENGTH;
  const label = opts.requiredLabel ? t(opts.requiredLabel) : t('nouns.password');
  return Yup.string()
    .required(`${label} ${t('validation:errors.required')}`)
    .test(
      'min_characters',
      `${label} ${t('validation:errors.too_short', { count: minLength })}`,
      value => !value || value.length >= minLength
    )
    .test('one_lowercase', `${label} ${t('validation:errors.no_lowercase')}`, value => !value || /[a-z]/.test(value))
    .test('one_uppercase', `${label} ${t('validation:errors.no_uppercase')}`, value => !value || /[A-Z]/.test(value))
    .test('one_number', `${label} ${t('validation:errors.no_number')}`, value => !value || /[0-9]/.test(value))
    .test(
      'one_special_character',
      `${label} ${t('validation:errors.no_special_chars')}`,
      value => !value || /[^a-zA-Z0-9&]/.test(value)
    );
}

/**
 * Yup validation for confirm password (must match ref).
 * @param {function} t
 * @param {string} [ref='password'] - field name to match
 */
export function createConfirmPasswordValidation(t, ref = 'password') {
  return Yup.string()
    .oneOf([Yup.ref(ref), null], t('validation:errors.passwords_mismatch'))
    .required(`${t('nouns.confirm_password')} ${t('validation:errors.required')}`);
}

/**
 * Schema for profile change-password: old_password, password, validate_password.
 */
export function createProfilePasswordSchema(t, minLength = DEFAULT_MIN_LENGTH) {
  return Yup.object().shape({
    old_password: Yup.string().required(
      `${t('nouns.current_password')} ${t('validation:errors.required')}`
    ),
    password: createNewPasswordValidation(t, { minLength, requiredLabel: 'nouns.new_password' }),
    validate_password: createConfirmPasswordValidation(t, 'password'),
  });
}

/**
 * Schema for register/activate: password, confirm_password (or confirm_password / password_confirmation).
 */
export function createRegisterPasswordSchema(t, minLength = DEFAULT_MIN_LENGTH) {
  return Yup.object().shape({
    password: createNewPasswordValidation(t, { minLength }),
    confirm_password: createConfirmPasswordValidation(t, 'password'),
  });
}

/**
 * Schema for reset/update password (no old password): password, password_confirmation.
 */
export function createResetPasswordSchema(t, minLength = DEFAULT_MIN_LENGTH) {
  return Yup.object().shape({
    password: createNewPasswordValidation(t, { minLength }),
    password_confirmation: createConfirmPasswordValidation(t, 'password'),
  });
}
